import { Button, Flex, Select, Space, Table, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {Link, useLocation, useParams} from "react-router-dom";
import { StyledButton, StyledTableFilterSelect } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent.jsx";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteTeamGroup } from "../../entities/api";
import { useFetchTeamGroupsPagination } from "~entities/api/useTeamGroupsAPI.js";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination.js";
import { Statuses } from "~utilities/constants.js";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { ExportModal } from "~components/ExportModal/ExportModal.tsx";
import { useExportEntityGroups, useImportEntityGroups } from "~pages/EntityGroups/hooks/index.ts";
import { defaultEntityGroupsExportFieldNames, entityGroupsExportFields } from "~pages/EntityGroups/constants/exportFields.ts";
import SearchFilter from "~components/SearchFilter/SearchFilter";

const PermissionName = PermissionNames.ENTITY_GROUP;

const columnsOne = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameFullLocal",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "status",
        defaultMessage: "Status",
      }),
      dataIndex: "status",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/entity-groups/manage/${record.entityGroupId}/`}>
              <StyledButton type="text">
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className="fas fa-trash-can" />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const statusFilter = [
  {
    text: "All",
    value: Statuses.ALL,
  },
  {
    text: "Active Clubs",
    value: Statuses.ACTIVE,
  },
  {
    text: "Inactive Clubs",
    value: Statuses.INACTIVE,
  },
  {
    text: "Pending Clubs",
    value: Statuses.PENDING,
  },
];

const ClubList = () => {
  const { formatMessage } = useIntl();
  const { customerId, sport, organizationId } = useParams();
  const location = useLocation();
  const [selected, setSelected] = useState({});
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [activeFilter, setActiveFilter] = useState(Statuses.ALL);
  const { mutate: deleteFn, isPending } = useDeleteTeamGroup();

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(50);

  const { data, isLoading } = useFetchTeamGroupsPagination({
    limit: pageSize,
    offset,
    ...(activeFilter && activeFilter !== "ALL" ? { status: activeFilter } : {}),
    ...(searchText ? { nameFullLocalContains: searchText } : {}),
  });

  const { mutateAsync: startImporting } = useImportEntityGroups();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportEntityGroups();

  const club = useMemo(() => data?.data || [], [data?.data]);
  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  useEffect(() => {
    setFilteredData(club);
  }, [club]);

  const handleOpenConfirmation = (record) => {
    setSelected(record);
    setShowConfirmation(true);
  };

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Club"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={() => deleteFn(selected?.entityGroupId)}
            handleCancel={() => setSelected({})}
            okText="Delete"
          >
            <div>{`You are about to delete ${selected.nameFullLocal}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>Clubs</Typography.Title>
        <Space>
          <StyledTableFilterSelect
            style={{ width: "140px" }}
            value={activeFilter}
            onChange={(value) => setActiveFilter(value)}
            variant="borderless"
          >
            {statusFilter.map((item) => (
              <Select.Option key={item.value} disabled={activeFilter === item.value} value={item.value}>
                <strong>{item.text}</strong>
              </Select.Option>
            ))}
          </StyledTableFilterSelect>
          <SearchFilter searchValue={searchText} doSearch={setSearchText} />
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE, PermissionOperations.UPDATE ]}>
            <ImportModal
                title={formatMessage({ id: "import.modal.title.entityGroups" })}
                description={formatMessage({ id: "import.modal.text.entityGroups" })}
                startImporting={startImporting}
            />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.READ ]}>
            <ExportModal
                handleExport={startExport}
                defaultFields={defaultEntityGroupsExportFieldNames}
                groupedFields={entityGroupsExportFields}
                localStorageKey="export.entityGroups"
                loading={isExportPending}
            />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/entity-groups/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1" />}>
                Club
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size="small"
        columns={columnsOne({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey="entityGroupId"
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + club.length + 1 : club.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
        dataSource={filteredData}
      />
    </Flex>
  );
};

export default ClubList;
